// Setup Assistant "follow along": while Lumi does the setup through chat, the
// app opens the section where each change lands so the user can watch it
// happen. Targets are plain onboarding actions ({tab, intent, store_id}) fed
// to handleOnboardingAction; nothing here opens modals or advances steps.

// Narrow-screen guard: Lumi floats in the bottom-right corner, and below this
// width the page behind it is too cramped for the opened section to be useful.
export const FOLLOW_ALONG_MIN_WIDTH = 1024;

const LOCATIONS_TAB = "locationsIntegrationsHoursSettings";

// Checklist step -> screen. Deliberately a static map (not the checklist's
// primaryAction): the roles step's primary action opens the POS import modal,
// which is wrong while Lumi creates roles herself.
// The POS steps use the plain "integrations" intent: "connect_pos" and
// "import_stores" open the page's own modals, which would put the flow back
// in the UI while Lumi is the one connecting and importing.
const TASK_TARGETS = {
  company_pos_connected: { tab: LOCATIONS_TAB, intent: "integrations", label: "Integrations" },
  store_pos_linked: { tab: LOCATIONS_TAB, intent: "integrations", label: "Integrations" },
  real_location_added: { tab: LOCATIONS_TAB, intent: "manage_locations", label: "Locations" },
  location_settings_reviewed: { tab: LOCATIONS_TAB, intent: "manage_locations", label: "Locations" },
  hours_of_operation_reviewed: { tab: LOCATIONS_TAB, intent: "hours_of_operation", label: "Hours" },
  labor_law_settings_reviewed: { tab: LOCATIONS_TAB, intent: "labor_laws", label: "Labor Laws" },
  departments_defined: { tab: "departmentRoleStationSettings", intent: "departments", label: "Departments" },
  roles_reviewed: { tab: "departmentRoleStationSettings", intent: "roles", label: "Roles" },
  employees_added: { tab: "employeesInformation", label: "Team Members" },
  welcome_message_sent: { tab: "employeesInformation", label: "Team Members" },
  training_content_started: { tab: "createTrainingContent", label: "Training Content" },
  labor_setup_started: { tab: "laborModel", label: "Labor Model" },
  billing_reviewed: { tab: "billingSettings", label: "Billing" },
};

// Setup tool -> checklist step whose screen shows the result.
const TOOL_TASK_KEYS = {
  request_pos_credentials: "company_pos_connected",
  import_pos_location: "real_location_added",
  create_location: "real_location_added",
  set_location_address: "location_settings_reviewed",
  update_store_settings: "location_settings_reviewed",
  set_store_hours: "hours_of_operation_reviewed",
  set_store_jurisdiction: "labor_law_settings_reviewed",
  set_labor_law_monitoring: "labor_law_settings_reviewed",
  set_departments: "departments_defined",
  create_roles: "roles_reviewed",
  create_role: "roles_reviewed",
  map_pos_job: "roles_reviewed",
  invite_employee: "employees_added",
  resend_welcome_message: "welcome_message_sent",
  import_roster: "employees_added",
  request_roster_upload: "employees_added",
  import_pos_employees: "employees_added",
  set_billing_contact: "billing_reviewed",
  // No screen of its own: the starting-modules step has no TASK_TARGETS
  // entry, so confirming the card changes the nav without navigating.
  set_starting_modules: "starting_modules_selected",
};

export function followAlongTargetForTask(taskKey, storeId) {
  const target = TASK_TARGETS[taskKey];
  if (!target) return null;
  const storeNumber = Number(storeId);
  return storeNumber > 0 ? { ...target, store_id: storeNumber } : { ...target };
}

// First tool with a known screen wins; a mixed batch (department + roles)
// still lands on one page.
export function followAlongTargetForTools(toolNames, storeId) {
  for (const name of Array.isArray(toolNames) ? toolNames : []) {
    const taskKey = TOOL_TASK_KEYS[name];
    if (taskKey) return followAlongTargetForTask(taskKey, storeId);
  }
  return null;
}

export function isSameFollowAlongTarget(a, b) {
  if (!a || !b) return false;
  return (
    a.tab === b.tab &&
    (a.intent || null) === (b.intent || null) &&
    (Number(a.store_id) || null) === (Number(b.store_id) || null)
  );
}

export function followAlongViewportAllows(width) {
  const w = Number.isFinite(width) ? width : (typeof window !== "undefined" ? window.innerWidth : 0);
  return w >= FOLLOW_ALONG_MIN_WIDTH;
}
